import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type EinkaufStatsItem, type WerkstoffAnalyse, type EinkaufAufgabe } from '../lib/api';
import { MailModal, MAIL_SIGNATUR, type MailDraft } from '../components/MailEntwurf';
import { AutoVervollstaendigung } from '../components/AutoVervollstaendigung';
import { NeuBanner, NeuEtikett, useEinkaufNeu } from '../components/EinkaufNeu';

// Einkauf-Dashboard (E3): KPIs, Top-Werkstoffe/-Lieferanten, Monatstrend,
// Aktionslisten (wartet auf Antwort / überfällig / diese Woche erwartet).
// Diagramme: eine Serie → eine Farbe (validiert #2a78d6), dünne Balken,
// abgerundete Datenenden, Hover-Tooltips, Werte in Text-Ink (nie Serienfarbe).

const MARK = '#2a78d6';

function fmtDatum(v?: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

const MONAT_KURZ = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
function fmtMonat(ym: string): string {
  const y = ym.slice(0, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONAT_KURZ[m - 1] ?? ym} ${y.slice(2)}`;
}

// Mail-Vorlage je Listentyp (Modal/mailto in components/MailEntwurf).
function buildMail(art: 'ueberfaellig' | 'wartet' | 'erwartet', r: EinkaufStatsItem, email: string): MailDraft {
  const mat = `${r.menge ?? ''}x ${r.werkstoff ?? ''} ${r.format ?? ''}${r.dicke ? ` · ${r.dicke} mm` : ''}`.trim();
  const ref = r.referenz ? ` (${r.referenz})` : '';
  const gruss = 'Guten Tag,\n\n';
  const ende = '\n\nVielen Dank.\n\n' + MAIL_SIGNATUR;
  if (art === 'ueberfaellig') {
    return {
      an: email,
      betreff: `Lieferverzug – Bestellung vom ${fmtDatum(r.bestell_datum)}: ${mat}${ref}`,
      text: gruss +
        `zu unserer Bestellung vom ${fmtDatum(r.bestell_datum)} über ${mat}${ref} war der Liefertermin ` +
        `${fmtDatum(r.liefertermin)} zugesagt — die Ware ist bei uns noch nicht eingegangen (${r.tage ?? '?'} Tage überfällig).\n\n` +
        `Bitte teilen Sie uns kurzfristig den aktuellen Versandstatus und den neuen Liefertermin mit.` +
        (r.bestellnr ? `\nIhre Referenz/AB: ${r.bestellnr}` : '') + ende,
    };
  }
  if (art === 'erwartet') {
    return {
      an: email,
      betreff: `${r.ab_bestaetigt ? 'Lieferung diese Woche' : 'Auftragsbestätigung ausstehend'} – ${mat}${ref}`,
      text: gruss +
        `zu unserer Bestellung vom ${fmtDatum(r.bestell_datum)} über ${mat}${ref} ist die Lieferung für ` +
        `${fmtDatum(r.liefertermin)} angekündigt.\n\n` +
        (r.ab_bestaetigt
          ? `Bitte bestätigen Sie uns kurz, dass der Termin gehalten wird.`
          : `Uns liegt hierzu noch keine Auftragsbestätigung vor — bitte senden Sie uns die AB und bestätigen Sie den Liefertermin.`) + ende,
    };
  }
  return {
    an: email,
    betreff: `Nachfrage zu unserer Anfrage${r.anfrage_datum ? ` vom ${fmtDatum(r.anfrage_datum)}` : ''}: ${mat}${ref}`,
    text: gruss +
      `wir hatten ${mat}${ref} bei Ihnen angefragt${r.anfrage_datum ? ` (Anfrage vom ${fmtDatum(r.anfrage_datum)})` : ''} ` +
      `und bisher keine Rückmeldung erhalten.\n\n` +
      `Bitte senden Sie uns Ihr Angebot mit Preis und Liefertermin — der Bedarf ist terminkritisch${r.termin ? ` (gewünscht: ${r.termin})` : ''}.` + ende,
  };
}

// Modul-Header zeigt Datum + KW; die persönliche Begrüßung lebt zentral im Portal.
// ISO-8601 Kalenderwoche (= deutsche KW).
function isoKW(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round((t.getTime() - firstThu.getTime()) / (7 * 24 * 3600 * 1000));
}
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export function EinkaufDashboard() {
  const stats = useApi(() => api.einkauf.stats(), []);
  const lieferanten = useApi(() => api.lieferanten.list(), []);
  const [mail, setMail] = useState<MailDraft | null>(null);
  const d = stats.data;

  // Live: Kennzahlen/Listen alle 30 s still nachladen (nur bei sichtbarem Tab), Stand-Zeit anzeigen
  const reloadRef = useRef(stats.reload);
  reloadRef.current = stats.reload;
  const [stand, setStand] = useState(() => new Date());
  useEffect(() => { if (stats.data) setStand(new Date()); }, [stats.data]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') reloadRef.current(); }, 30_000);
    const vis = () => { if (document.visibilityState === 'visible') reloadRef.current(); };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, []);

  const now = new Date();

  const emailFuer = (name?: string | null): string => {
    if (!name) return '';
    const n = name.trim().toLowerCase();
    const hit = lieferanten.data?.data.find(l =>
      l.name.toLowerCase() === n || n.startsWith(l.name.toLowerCase()) || l.name.toLowerCase().startsWith(n));
    return hit?.email ?? '';
  };
  const openMail = (art: 'ueberfaellig' | 'wartet' | 'erwartet') => (r: EinkaufStatsItem) => {
    setMail(buildMail(art, r, emailFuer(r.lieferant)));
    // Mahnwesen (SAP-Muster): jede Mahn-Mail wird automatisch gezählt und datiert
    if (art === 'ueberfaellig') {
      void api.blechbedarf.update(r.id, {
        mahnungen: (r.mahnungen ?? 0) + 1,
        letzte_mahnung: new Date().toISOString().slice(0, 10),
      }).then(() => stats.reload()).catch(() => undefined);
    }
  };

  // ✓ Ware angekommen: Wareneingang heute + fertig — direkt aus der Liste
  const wareDa = async (r: EinkaufStatsItem) => {
    const was = `${r.menge ?? ''} ${r.werkstoff ?? ''} ${r.format ?? ''}`.trim();
    if (!confirm(`Wareneingang heute buchen?\n${was}${r.lieferant ? ` · ${r.lieferant}` : ''}`)) return;
    const heute = new Date().toISOString().slice(0, 10);
    try {
      await api.blechbedarf.update(r.id, { wareneingang: heute, menge_angekommen: r.menge ?? null, fertig: 1 });
      stats.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  // Erledigt-Haken für "Wartet auf Antwort" — Bedarf hat sich erledigt (storniert,
  // anderweitig besorgt o.ä.), ohne dass er als "Ware angekommen" gebucht wird.
  const erledigt = async (r: EinkaufStatsItem) => {
    const was = `${r.menge ?? ''} ${r.werkstoff ?? ''} ${r.format ?? ''}`.trim();
    if (!confirm(`Als erledigt markieren?\n${was}${r.referenz ? ` · ${r.referenz}` : ''}`)) return;
    try {
      await api.blechbedarf.update(r.id, { fertig: 1 });
      stats.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  // Was sagt der Lieferant? Grund + neuer Liefertermin direkt aus der Liste
  const notiz = async (r: EinkaufStatsItem) => {
    const grund = prompt(`Was sagt ${r.lieferant ?? 'der Lieferant'}? (Grund für die Verspätung, in den Kommentar)`, r.kommentar ?? '');
    if (grund === null) return;
    const neuerTermin = prompt('Neuer Liefertermin (z. B. KW32, 15.08.2026) — leer lassen zum Beibehalten:', '');
    if (neuerTermin === null) return;
    try {
      const patch: Partial<import('../lib/api').Blechbedarf> = {};
      const g = grund.trim();
      if (g) patch.kommentar = /achtung/i.test(g) ? g : `Achtung: ${g}`;
      if (neuerTermin.trim()) patch.liefertermin = neuerTermin.trim();
      if (Object.keys(patch).length) { await api.blechbedarf.update(r.id, patch); stats.reload(); }
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  return (
    <div className="p-4 md:p-8">
      <header className="mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
        <Link to="/banf" data-testid="dash-sammel"
              className="bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 shadow-sm">
          📋 Bestellanforderung (BANF)
        </Link>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <span className="text-sm text-slate-500">
            {WOCHENTAGE[now.getDay()] ?? ''}, {String(now.getDate()).padStart(2, '0')}.{String(now.getMonth() + 1).padStart(2, '0')}.{now.getFullYear()}
            {' · '}
            <span className="font-semibold text-brand">KW {isoKW(now)}</span>
            {' · '}
            <span data-testid="dash-live" className="text-emerald-600" title="Das Dashboard aktualisiert sich automatisch alle 30 Sekunden">
              ● live · Stand {String(stand.getHours()).padStart(2, '0')}:{String(stand.getMinutes()).padStart(2, '0')}:{String(stand.getSeconds()).padStart(2, '0')}
            </span>
            {typeof d?.kpi.woche === 'number' && d.kpi.woche > 0 && (
              <> · <span className="font-medium text-slate-700">{d.kpi.woche}</span> Lieferung{d.kpi.woche === 1 ? '' : 'en'} diese Woche erwartet</>
            )}
          </span>
        </div>
      </header>

      <NeuBanner />
      <RueckfrageKachel />
      {stats.loading && !d && <div className="text-slate-500">Lädt …</div>}
      {stats.error && <div className="text-red-600">Fehler: {stats.error.message}</div>}

      {d && <>
        {/* KPI-Kacheln */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <Kpi label="Offene Anfragen (Blech)"
               value={Math.max(0, (d.kpi.offen ?? 0) - (d.kpi.offen_profilrohr ?? 0) - (d.kpi.offen_sonstiges ?? 0))}
               tone={(d.kpi.offen_alt ?? 0) > 0 ? 'amber' : 'default'}
               sub={(d.kpi.offen_alt ?? 0) > 0 ? `${d.kpi.offen_alt} warten > 3 Tage` : 'warten auf Antwort'}
               to="/blechbedarf?status=unbestellt" />
          <Kpi label="Bestellt · unterwegs" value={d.kpi.unterwegs ?? 0} sub="bestellt in den letzten 90 Tagen" to="/blechbedarf?status=bestellt" />
          <Kpi label="AB fehlt" value={d.kpi.ohne_ab ?? 0}
               tone={(d.kpi.ohne_ab ?? 0) > 0 ? 'amber' : 'default'} sub="ohne AB · letzte 90 Tage" to="/blechbedarf?status=bestellt" />
          <Kpi label="Überfällig" value={d.kpi.ueberfaellig ?? 0}
               tone={(d.kpi.ueberfaellig ?? 0) > 0 ? 'red' : 'default'} sub="Liefertermin verpasst" to="/blechbedarf?status=bestellt" />
          <Kpi label="Diese Woche" value={d.kpi.woche ?? 0} sub="Lieferungen erwartet" to="/blechbedarf?status=bestellt" />
          <Kpi label="Neu (30 Tage)" value={d.kpi.monat_neu ?? 0} sub="neue Bedarfe" to="/blechbedarf?status=alle" />
          <Kpi label="Profile & Rohre" value={d.kpi.offen_profilrohr ?? 0} sub="offen · eigene Ansicht" to="/profile-rohre" />
          <Kpi label="Sonstiges" value={d.kpi.offen_sonstiges ?? 0} sub="Fittings offen" to="/sonstiges" />
          <Kpi label="Ø Bearbeitungszeit Einkauf" value={d.kpi.dlz_anfrage ?? 0} einheit="Tage"
               sub="von Anfrage bis Bestellung · Ø letzte 90 Tage"
               hint={"Wie schnell der Einkauf selbst arbeitet: durchschnittliche Zeit zwischen dem Versand der Anfrage an die Lieferanten und der Bestellung.\nSteigt dieser Wert, kommen Angebote zu langsam oder Entscheidungen bleiben liegen.\n(SAP: Bearbeitungszeit Einkauf)"}
               to="/blechbedarf?status=angefragt" />
          <Kpi label="Ø Wiederbeschaffungszeit" value={d.kpi.dlz_lieferzeit ?? 0} einheit="Tage"
               sub="von Bestellung bis Wareneingang · Ø 90 Tage"
               hint={"Wie schnell die Lieferanten liefern: durchschnittliche Zeit von der Bestellung bis zum Wareneingang.\nFaustregel für die Planung: spätester Bestellzeitpunkt = Bedarfstermin minus diese Zahl.\n(SAP: Wiederbeschaffungszeit / Planlieferzeit)"}
               to="/lieferanten#bewertung" />
        </div>

        {/* Aktionslisten — das Wichtigste zuerst */}
        <AufgabenSektion />
        {d.ueberfaellig.length > 0 && (
          <ActionListe title="⚠ Überfällig — Lieferant kontaktieren" accent="red" items={d.ueberfaellig}
            cols={r => [fmtDatum(r.liefertermin_datum ?? r.liefertermin ?? null), `${r.tage}T überfällig`, r.lieferant ?? '—',
              r.kommentar ? `📝 ${r.kommentar}` : (r.mahnungen ?? 0) > 0 ? `${r.mahnungen}× gemahnt · ${fmtDatum(r.letzte_mahnung)}` : 'noch nicht gemahnt']}
            onMail={openMail('ueberfaellig')} onWareDa={wareDa} onNotiz={notiz} />
        )}
        {d.wartet.length > 0 && (
          <ActionListe title="Wartet auf Antwort / noch nicht bestellt" accent="amber" items={d.wartet}
            cols={r => [fmtDatum(r.datum), r.tage != null ? `seit ${r.tage}T` : '—', r.lieferanten_angefragt ? 'angefragt' : 'noch nicht angefragt']}
            onMail={openMail('wartet')} onErledigt={erledigt} />
        )}
        {d.erwartet.length > 0 && (
          <ActionListe title="Diese Woche erwartet" accent="sky" items={d.erwartet}
            cols={r => [fmtDatum(r.liefertermin_datum ?? r.liefertermin ?? null), r.lieferant ?? '—', r.ab_bestaetigt ? 'AB ✓' : 'AB fehlt']}
            onMail={openMail('erwartet')} onWareDa={wareDa} />
        )}

        {/* Diagramme */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <BalkenKarte title="Top-Werkstoffe" sub="Käufe der letzten 12 Monate — klicken öffnet die Auswertung in Blechbedarf"
            rows={d.topWerkstoffe.map(w => ({ label: w.werkstoff, n: w.n }))}
            linkFuer={label => `/blechbedarf?status=alle&werkstoff=${encodeURIComponent(label)}`} />
          <BalkenKarte title="Top-Lieferanten" sub="Käufe der letzten 12 Monate"
            rows={d.topLieferanten.map(l => ({ label: l.lieferant, n: l.n }))}
            linkFuer={() => '/lieferanten'} />
        </div>
        <TrendKarte trend={d.trend} />

        {/* Werkstoff-Analyse: Auswahl → Käufe & Preise pro Jahr, Lieferanten, letzte Käufe */}
        <WerkstoffAnalyseKarte />
      </>}

      {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} />}
    </div>
  );
}

function WerkstoffAnalyseKarte() {
  const werkstoffe = useApi(() => api.einkauf.werkstoffe(), []);
  const [gewaehlt, setGewaehlt] = useState('');
  const [eingabe, setEingabe] = useState('');
  const analyse = useApi<WerkstoffAnalyse | null>(
    () => gewaehlt ? api.einkauf.analyse(gewaehlt) : Promise.resolve(null), [gewaehlt]);
  const a = analyse.data;
  const jahre = a?.jahre ?? [];
  const maxN = Math.max(1, ...jahre.map(j => j.n));
  const maxP = Math.max(0.01, ...jahre.map(j => j.avg_kg ?? 0));

  // Preistrend: erstes vs. letztes Jahr mit gültigem Ø kg-Preis
  const mitPreis = jahre.filter(j => j.avg_kg != null && j.avg_kg > 0);
  const preisTrend = mitPreis.length >= 2 ? (() => {
    const von = mitPreis[0]!, bis = mitPreis[mitPreis.length - 1]!;
    return { von: von.avg_kg!, bis: bis.avg_kg!, vonJahr: von.jahr, bisJahr: bis.jahr,
             pct: ((bis.avg_kg! - von.avg_kg!) / von.avg_kg!) * 100 };
  })() : null;
  // günstigster Lieferant (Ø €/kg) für die Hervorhebung
  const guenstigsterKg = Math.min(...(a?.lieferanten ?? []).map(l => l.avg_kg ?? Infinity));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-semibold text-slate-900">Werkstoff-Analyse</h2>
          <div className="text-xs text-slate-500">Werkstoff wählen → Käufe & kg-Preise über die Jahre (inkl. Archiv seit 2021)</div>
        </div>
        <div className="flex gap-2">
          <div className="min-w-[220px]" onKeyDown={e => { if (e.key === 'Enter') setGewaehlt(eingabe.trim()); }}>
            <AutoVervollstaendigung value={eingabe} onChange={setEingabe}
              optionen={(werkstoffe.data?.data ?? []).map(w => w.werkstoff)}
              placeholder="Werkstoff (z. B. 1.4301) …"
              onAuswahl={v => setGewaehlt(v.trim())} />
          </div>
          <button className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90"
                  onClick={() => setGewaehlt(eingabe.trim())}>Analysieren</button>
        </div>
      </div>

      {analyse.loading && gewaehlt && <div className="text-slate-500 text-sm">Lädt …</div>}
      {a && (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <span className="bg-slate-100 rounded-lg px-3 py-1.5"><b>{a.werkstoff}</b></span>
            <span className="bg-slate-100 rounded-lg px-3 py-1.5">{a.summe?.n_gesamt ?? 0} Käufe gesamt</span>
            {a.summe?.avg_kg_12m != null && (
              <span className="bg-slate-100 rounded-lg px-3 py-1.5">Ø {a.summe.avg_kg_12m.toFixed(2).replace('.', ',')} €/kg (12 Monate)</span>
            )}
            {preisTrend && (
              <span className={`rounded-lg px-3 py-1.5 font-semibold ${
                preisTrend.pct > 2 ? 'bg-red-50 text-red-700'
                : preisTrend.pct < -2 ? 'bg-emerald-50 text-emerald-700'
                : 'bg-slate-100 text-slate-600'}`}
                title={`Ø kg-Preis ${preisTrend.vonJahr}: ${preisTrend.von.toFixed(2)} € → ${preisTrend.bisJahr}: ${preisTrend.bis.toFixed(2)} €`}>
                {preisTrend.pct > 2 ? '↑' : preisTrend.pct < -2 ? '↓' : '→'} {preisTrend.pct > 0 ? '+' : ''}{preisTrend.pct.toFixed(0)} %
                <span className="font-normal text-xs ml-1">Preis {preisTrend.vonJahr}→{preisTrend.bisJahr}</span>
              </span>
            )}
          </div>

          {/* Zwei getrennte Diagramme (eine Achse pro Diagramm): Käufe/Jahr und Ø-Preis/Jahr */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <JahresBalken title="Käufe pro Jahr" rows={jahre.map(j => ({
              jahr: j.jahr, wert: j.n, max: maxN, label: String(j.n),
              tip: `${j.jahr}: ${j.n} Käufe${j.menge_sum ? ` · Menge ~${j.menge_sum}` : ''}` }))} />
            <JahresBalken title="Ø kg-Preis pro Jahr" rows={jahre.map(j => ({
              jahr: j.jahr, wert: j.avg_kg ?? 0, max: maxP,
              label: j.avg_kg != null ? `${j.avg_kg.toFixed(2).replace('.', ',')} €` : '—',
              tip: `${j.jahr}: Ø ${j.avg_kg ?? '—'} €/kg` + (j.min_kg != null ? ` (${j.min_kg}–${j.max_kg})` : '') }))} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Lieferanten</h3>
              <table className="w-full text-[13px]">
                <thead className="text-xs text-slate-500 uppercase"><tr>
                  <th className="text-left py-1">Lieferant</th><th className="text-right py-1">Käufe</th>
                  <th className="text-right py-1">Ø €/kg</th><th className="text-right py-1">letzter</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {a.lieferanten.map(l => {
                    const guenstig = l.avg_kg != null && l.avg_kg === guenstigsterKg && a.lieferanten.length > 1;
                    return (
                    <tr key={l.lieferant} className={guenstig ? 'bg-emerald-50' : undefined}>
                      <td className="py-1">{l.lieferant}{guenstig && <span className="ml-1.5 text-emerald-700 text-[11px] font-semibold">★ günstigster</span>}</td>
                      <td className="py-1 text-right tabular-nums">{l.n}</td>
                      <td className={`py-1 text-right tabular-nums ${guenstig ? 'font-semibold text-emerald-700' : ''}`}>{l.avg_kg != null ? l.avg_kg.toFixed(2).replace('.', ',') : '—'}</td>
                      <td className="py-1 text-right whitespace-nowrap">{fmtDatum(l.letzter)}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Letzte Käufe</h3>
              <table className="w-full text-[13px]">
                <tbody className="divide-y divide-slate-100">
                  {a.letzte.map(k => (
                    <tr key={k.id}>
                      <td className="py-1 whitespace-nowrap">{fmtDatum(k.datum)}</td>
                      <td className="py-1">{k.menge} {k.form ?? ''} {k.format ?? ''} {k.dicke ? `· ${k.dicke}mm` : ''}</td>
                      <td className="py-1">{k.lieferant ?? '—'}</td>
                      <td className="py-1 text-right whitespace-nowrap tabular-nums">
                        {k.preis != null ? `${k.preis.toLocaleString('de-DE')} €${k.preis_einheit ? `/${k.preis_einheit}` : ''}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Jahresbalken: eine Serie/eine Farbe, Wert-Label in Text-Ink über dem Balken
function JahresBalken({ title, rows }: {
  title: string; rows: { jahr: string; wert: number; max: number; label: string; tip: string }[];
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div className="flex items-end gap-3 h-32">
        {rows.map(r => (
          <div key={r.jahr} className="flex-1 flex flex-col items-center justify-end h-full" title={r.tip}>
            <div className="text-[11px] text-slate-700 font-semibold mb-0.5 tabular-nums">{r.label}</div>
            <div className="w-full rounded-t" style={{
              height: `${Math.max(3, (r.wert / r.max) * 78)}%`, background: MARK }} />
            <div className="text-[11px] text-slate-500 mt-1">{r.jahr}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = 'default', to, einheit, hint }: {
  label: string; value: number; sub?: string; tone?: 'default' | 'red' | 'amber'; to?: string;
  einheit?: string; hint?: string;
}) {
  const color = tone === 'red' && value > 0 ? 'text-red-600' : tone === 'amber' && value > 0 ? 'text-amber-600' : 'text-slate-900';
  const inner = (
    <div className="bg-white rounded-xl p-4 border border-slate-200 h-full hover:shadow-sm" title={hint}>
      <div className="text-xs text-slate-500 uppercase leading-tight">{label}{hint && <span className="ml-1 text-slate-300">ⓘ</span>}</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>{value}{einheit && <span className="text-base font-semibold text-slate-400 ml-1">{einheit}</span>}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

// Horizontale Rangliste: dünne Balken (h-3.5), 4px abgerundetes Datenende,
// Wert in Text-Ink am Balkenende, Hover-Tooltip via title.
function BalkenKarte({ title, sub, rows, linkFuer }: {
  title: string; sub: string; rows: { label: string; n: number }[];
  linkFuer?: (label: string) => string;
}) {
  const max = Math.max(1, ...rows.map(r => r.n));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="mb-3">
        <h2 className="font-semibold text-slate-900">{title}</h2>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500">Keine Daten</div>}
        {rows.map(r => {
          const zeile = (
            <div className="flex items-center gap-2" title={`${r.label}: ${r.n} Käufe`}>
              <div className="w-32 text-[13px] text-slate-700 truncate text-right shrink-0">{r.label}</div>
              <div className="flex-1 h-3.5 relative">
                <div className="absolute inset-y-0 left-0 rounded-r"
                     style={{ width: `${(r.n / max) * 100}%`, background: MARK, minWidth: 3 }} />
              </div>
              <div className="w-12 text-[13px] text-slate-700 tabular-nums shrink-0">{r.n}</div>
            </div>
          );
          return linkFuer
            ? <Link key={r.label} to={linkFuer(r.label)} className="block rounded hover:bg-slate-50">{zeile}</Link>
            : <div key={r.label}>{zeile}</div>;
        })}
      </div>
    </div>
  );
}

// Monatstrend: vertikale Balken (SVG), eine Serie, recessive Grundlinie,
// selektive Labels (nur Maximum + letzter Monat), Hover-Tooltip via <title>.
function TrendKarte({ trend }: { trend: { monat: string; n: number }[] }) {
  if (!trend.length) return null;
  const W = 900, H = 180, PAD = 24, GAP = 2;
  const max = Math.max(1, ...trend.map(t => t.n));
  const bw = (W - PAD * 2) / trend.length;
  const maxIdx = trend.findIndex(t => t.n === max);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
      <div className="mb-2">
        <h2 className="font-semibold text-slate-900">Einkäufe pro Monat</h2>
        <div className="text-xs text-slate-500">letzte 24 Monate · Anzahl Bedarfe</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H + 22}`} className="w-full" role="img" aria-label="Einkäufe pro Monat">
        <line x1={PAD} y1={H} x2={W - PAD} y2={H} stroke="#e2e8f0" strokeWidth="1" />
        {trend.map((t, i) => {
          const h = Math.max(2, (t.n / max) * (H - 24));
          const x = PAD + i * bw;
          const label = i === maxIdx || i === trend.length - 1;
          return (
            <g key={t.monat}>
              <rect x={x + GAP / 2} y={H - h} width={Math.max(2, bw - GAP)} height={h} rx="3" fill={MARK}>
                <title>{`${fmtMonat(t.monat)}: ${t.n}`}</title>
              </rect>
              {label && (
                <text x={x + bw / 2} y={H - h - 6} textAnchor="middle" fontSize="11" fill="#334155" fontWeight="600">{t.n}</text>
              )}
              {i % 3 === 0 && (
                <text x={x + bw / 2} y={H + 14} textAnchor="middle" fontSize="10" fill="#94a3b8">{fmtMonat(t.monat)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ActionListe({ title, accent, items, cols, onMail, onWareDa, onNotiz, onErledigt }: {
  title: string; accent: 'red' | 'amber' | 'sky'; items: EinkaufStatsItem[];
  cols: (r: EinkaufStatsItem) => string[];
  onMail?: (r: EinkaufStatsItem) => void;
  onWareDa?: (r: EinkaufStatsItem) => void;
  onNotiz?: (r: EinkaufStatsItem) => void;
  onErledigt?: (r: EinkaufStatsItem) => void;
}) {
  const s = {
    red:   { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-700',  borderL: 'border-l-red-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', borderL: 'border-l-amber-500' },
    sky:   { bg: 'bg-sky-50',   border: 'border-sky-200',   text: 'text-sky-800',   borderL: 'border-l-sky-500' },
  }[accent];
  return (
    <div className={`${s.bg} border-2 ${s.border} border-l-[6px] ${s.borderL} rounded-xl overflow-hidden mb-6 shadow-sm`}>
      <div className="px-5 py-3 border-b-2 border-slate-200 bg-white/60 flex items-center justify-between">
        <h3 className={`font-bold text-sm ${s.text}`}>{title}</h3>
        <span className="text-xs font-medium text-slate-600">{items.length} Einträge</span>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-[13px] bg-white min-w-[640px]">
        <tbody className="divide-y divide-slate-100">
          {items.map(r => {
            const [c1, c2, c3, c4] = cols(r);
            return (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className={`px-4 py-1.5 whitespace-nowrap font-semibold ${s.text}`}>{c1}</td>
                <td className="px-4 py-1.5 whitespace-nowrap">{c2}</td>
                <td className="px-4 py-1.5">
                  <Link to="/blechbedarf" className="text-brand hover:underline">
                    {r.menge ?? ''} {r.werkstoff ?? '—'} {r.format ?? ''} {r.dicke ? `· ${r.dicke}mm` : ''}
                  </Link>
                  <span className="text-slate-500 ml-2">{r.referenz ?? ''}</span>
                </td>
                <td className="px-4 py-1.5 whitespace-nowrap text-slate-600">{c3}</td>
                {c4 != null && <td className="px-2 py-1.5 whitespace-nowrap text-xs text-slate-500">{c4}</td>}
                <td className="px-2 py-1.5 text-right w-24 whitespace-nowrap">
                  {onNotiz && (
                    <button onClick={() => onNotiz(r)} title="Notiz / neuen Liefertermin eintragen (was sagt der Lieferant?)"
                            className="text-slate-400 hover:text-amber-600 text-base leading-none mr-2">📝</button>
                  )}
                  {onWareDa && (
                    <button onClick={() => onWareDa(r)} title="Ware angekommen — Wareneingang heute buchen"
                            className="text-slate-400 hover:text-emerald-600 text-base leading-none mr-2">✓</button>
                  )}
                  {onErledigt && (
                    <button onClick={() => onErledigt(r)} title="Als erledigt markieren (verschwindet aus der Liste)"
                            className="text-slate-400 hover:text-emerald-600 text-base leading-none mr-2">✓</button>
                  )}
                  {onMail && (
                    <button onClick={() => onMail(r)} title="Mail an Lieferant vorbereiten"
                            className="text-slate-400 hover:text-brand text-base leading-none">✉</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// "Idee Einkauf": Aufgaben/Anforderungen an den Einkauf — wer etwas braucht,
// trägt es hier ein statt per Zuruf/Zettel. Status: offen → bestellt → erledigt.
function AufgabenSektion() {
  const aufgaben = useApi(() => api.einkaufAufgaben.list(true), []);
  const aufgabenReload = useRef(aufgaben.reload);
  aufgabenReload.current = aufgaben.reload;
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') aufgabenReload.current(); }, 30_000);
    return () => clearInterval(t);
  }, []);
  const { daten: neuDaten, gesehen: neuGesehen } = useEinkaufNeu();
  const neuIds = new Set(neuDaten.aufgaben.map(n => n.id));
  const [titel, setTitel] = useState('');
  const [von, setVon] = useState('');
  const [eilig, setEilig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zeigeErledigte, setZeigeErledigte] = useState(false);
  const alle = aufgaben.data?.data ?? [];
  const rows = alle.filter(a => a.status !== 'erledigt');
  // Erledigte der letzten 30 Tage — sichtbar auf Wunsch, mit „wieder öffnen“
  const erledigte = alle.filter(a => a.status === 'erledigt'
    && a.erledigt_at && Date.now() - Date.parse(a.erledigt_at) < 30 * 86_400_000);

  const alterTage = (a: EinkaufAufgabe) =>
    Math.floor((Date.now() - Date.parse(a.created_at)) / 86_400_000);

  const anlegen = async () => {
    if (!titel.trim()) return;
    setBusy(true);
    try {
      await api.einkaufAufgaben.create({ titel, angefordert_von: von || undefined, prio: eilig ? 'eilig' : 'normal' });
      setTitel(''); setEilig(false); aufgaben.reload();
    } finally { setBusy(false); }
  };
  const setzeStatus = async (a: EinkaufAufgabe, status: EinkaufAufgabe['status']) => {
    await api.einkaufAufgaben.update(a.id, { status });
    aufgaben.reload();
  };

  // „BANF → Bestellung“: Aufgabe in einen echten Bedarf überführen — sie
  // durchläuft dann den vollen Prozess (Anfrage → Preisspiegel → Bestellung).
  const alsBedarf = async (a: EinkaufAufgabe) => {
    if (!confirm(`„${a.titel}“ als Bedarf übernehmen?\nDie Aufgabe wird erledigt und der Bedarf durchläuft den normalen Einkaufsprozess.`)) return;
    try {
      const r = await api.blechbedarf.create({
        datum: new Date().toISOString().slice(0, 10),
        sb: (a.angefordert_von ?? '').slice(0, 10) || null,
        form: 'Sonstiges',
        referenz: a.titel,
        kommentar: `Aus Einkauf-Aufgabe #${a.id}${a.angefordert_von ? ` (${a.angefordert_von})` : ''}`,
        priority: a.prio === 'eilig' ? 'eilig' : 'normal',
      });
      await api.einkaufAufgaben.update(a.id, { status: 'erledigt', notiz: `→ Bedarf #${r.data.id}` });
      aufgaben.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
  };

  return (
    <div className="bg-white rounded-xl border border-violet-200 overflow-hidden">
      <div className="px-4 py-3 bg-violet-50 border-b border-violet-200 flex items-center justify-between">
        <h2 className="font-semibold text-violet-900">📌 Aufgaben an den Einkauf</h2>
        <span className="text-xs text-violet-700">{rows.length} offen</span>
      </div>
      <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-2 items-center bg-slate-50">
        <input className="input !py-1.5 flex-1 min-w-[220px]" placeholder="Was wird gebraucht? (z. B. 5x Trennscheiben 230mm)"
               value={titel} onChange={e => setTitel(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') void anlegen(); }} />
        <input className="input !py-1.5 w-32" placeholder="Wer?" value={von} onChange={e => setVon(e.target.value)} />
        <label className="flex items-center gap-1 text-sm text-slate-600">
          <input type="checkbox" checked={eilig} onChange={e => setEilig(e.target.checked)} /> eilig
        </label>
        <button onClick={() => void anlegen()} disabled={busy || !titel.trim()}
                className="bg-brand text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">
          ＋ Melden
        </button>
      </div>
      {aufgaben.loading && !aufgaben.data && <div className="px-4 py-4 text-sm text-slate-400">Lädt …</div>}
      {(!aufgaben.loading || !!aufgaben.data) && rows.length === 0 && (
        <div className="px-4 py-4 text-sm text-slate-400">Keine offenen Aufgaben. 🎉</div>
      )}
      {rows.map(a => {
        const tage = alterTage(a);
        const alt_warn = a.status === 'offen' && (a.prio === 'eilig' ? tage >= 1 : tage >= 3);
        return (
        <div key={a.id} data-testid={neuIds.has(a.id) ? 'aufgabe-neu' : undefined}
             onClick={neuIds.has(a.id) ? () => neuGesehen('aufgabe', [a.id]) : undefined}
             className={`px-4 py-2 border-b border-slate-50 last:border-b-0 flex flex-wrap items-center gap-2 text-sm ${neuIds.has(a.id) ? 'bg-red-50/60 cursor-pointer' : ''}`}>
          {neuIds.has(a.id) && <NeuEtikett />}
          {a.prio === 'eilig' && <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs font-semibold">EILIG</span>}
          <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${a.typ === 'anfrage' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`}>
            {a.typ === 'anfrage' ? '🟣 Anfrage' : '🔵 Bestellung'}
          </span>
          {a.bild_key && (
            <a href={api.einkaufAufgaben.bildUrl(a.id)} target="_blank" rel="noreferrer" title="Foto ansehen">
              <img src={api.einkaufAufgaben.bildUrl(a.id)} alt="" className="w-7 h-7 rounded object-cover border border-slate-200" />
            </a>
          )}
          <span className={a.status === 'bestellt' ? 'text-slate-400' : 'text-slate-800'}>{a.titel}</span>
          {a.ansprechpartner && <span className="text-xs text-slate-500">👤 {a.ansprechpartner}</span>}
          {a.notiz && <span className="text-xs text-slate-400">· {a.notiz}</span>}
          {tage > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${alt_warn ? 'bg-amber-100 text-amber-800 font-semibold' : 'text-slate-400'}`}>
              seit {tage}T
            </span>
          )}
          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">
            {a.angefordert_von || a.erfasst_von || '—'} · {fmtDatum(a.created_at.slice(0, 10))}
          </span>
          <button onClick={() => void alsBedarf(a)} title="In einen Bedarf überführen — voller Einkaufsprozess (Anfrage/Preisspiegel/Bestellung)"
                  className="text-xs border border-violet-300 text-violet-700 rounded px-2 py-0.5 hover:bg-violet-50 whitespace-nowrap">↗ als Bedarf</button>
          {a.status === 'offen'
            ? <button onClick={() => void setzeStatus(a, 'bestellt')}
                      className="text-xs border border-sky-300 text-sky-700 rounded px-2 py-0.5 hover:bg-sky-50 whitespace-nowrap">→ bestellt</button>
            : <span className="text-xs bg-sky-100 text-sky-800 rounded px-2 py-0.5 whitespace-nowrap">bestellt</span>}
          <button onClick={() => void setzeStatus(a, 'erledigt')}
                  className="text-xs border border-emerald-300 text-emerald-700 rounded px-2 py-0.5 hover:bg-emerald-50 whitespace-nowrap">✓ erledigt</button>
        </div>
        );
      })}
      {erledigte.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60">
          <button onClick={() => setZeigeErledigte(z => !z)} className="text-xs text-slate-500 hover:text-slate-700">
            {zeigeErledigte ? '▾' : '▸'} Erledigte anzeigen ({erledigte.length} · letzte 30 Tage)
          </button>
          {zeigeErledigte && erledigte.map(a => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 text-[13px] text-slate-400 py-1">
              <span className="line-through">{a.titel}</span>
              {a.notiz && <span className="text-violet-500 no-underline">{a.notiz}</span>}
              <span className="ml-auto whitespace-nowrap">
                {a.angefordert_von || '—'} · erledigt {a.erledigt_at ? fmtDatum(a.erledigt_at.slice(0, 10)) : ''}
              </span>
              <button onClick={() => void setzeStatus(a, 'offen')}
                      className="text-xs border border-slate-300 text-slate-600 rounded px-2 py-0.5 hover:bg-white whitespace-nowrap">↩ wieder öffnen</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Positionen aus BANFs, die auf die Freigabe vom Chef warten
function RueckfrageKachel() {
  const r = useApi(() => api.sammelzettel.rueckfragen(), []);
  const rows = r.data?.data ?? [];
  if (rows.length === 0) return null;
  return (
    <section className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="dash-rueckfragen">
      <h2 className="font-semibold text-amber-900">⏳ Wartet auf den Chef: {rows.length} Position{rows.length === 1 ? '' : 'en'}</h2>
      {r.data?.darfEntscheiden && <p className="text-xs text-amber-800 mt-0.5">Bitte in der jeweiligen BANF freigeben oder ablehnen.</p>}
      <ul className="mt-2 text-sm space-y-1">
        {rows.map(p => (
          <li key={p.id}>
            <Link to={`/banf/${p.zettel_id}`} className="text-brand hover:underline">BANF-{String(p.zettel_id).padStart(4, '0')} · {p.dv_nummer}</Link>
            {' — '}{p.menge} {p.einheit ?? ''} {p.bezeichnung} <span className="text-slate-500">· „{p.frage_text}“ ({p.frage_von ?? '—'})</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
